import { useState, useEffect, useRef } from 'react';

export default function TierChangeModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  user, 
  availableTiers,
  isProcessing,
  errorMessage,
}) {
  const [selectedTier, setSelectedTier] = useState('');
  const confirmButtonRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedTier(user?.subscription_tier_id || '');
      // Auto-focus confirm button when modal opens
      setTimeout(() => confirmButtonRef.current?.focus(), 100);
    }
  }, [isOpen, user]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !isProcessing) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, isProcessing, onClose]);

  const handleConfirm = () => {
    if (selectedTier && selectedTier !== user?.subscription_tier_id) {
      onConfirm(user.id, selectedTier);
    }
  };

  const formatPrice = (cents) => {
    if (cents === 0) return 'Free';
    return `$${(cents / 100).toFixed(2)}/mo`;
  };

  if (!isOpen || !user) return null;

  const currentTier = availableTiers.find(t => t.id === user.subscription_tier_id);
  const isChanged = selectedTier !== user.subscription_tier_id;

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={isProcessing ? undefined : onClose}
    >
      <div 
        className="bg-dark-800 border border-dark-700 rounded-lg max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-dark-700">
          <h2 className="text-xl font-bold text-white">Change Subscription Tier</h2>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-4">
            <p className="text-gray-300 mb-2">
              Changing subscription tier for: <span className="font-medium text-white">{user.display_name || user.displayName || user.twitch_username}</span>
            </p>
            <p className="text-sm text-gray-400">
              Current tier: <span className="font-medium">{currentTier?.name || 'None'}</span>
            </p>
          </div>

          {errorMessage && (
            <div className="mb-4 p-4 bg-red-900/20 border border-red-500/30 rounded-sm text-red-400 text-sm" role="alert">
              {errorMessage}
            </div>
          )}

          {/* Tier Selection */}
          <div>
            <label htmlFor="tier-select" className="block text-sm font-medium text-gray-400 mb-2">
              Select New Tier
            </label>
            <select
              id="tier-select"
              value={selectedTier}
              onChange={(e) => setSelectedTier(e.target.value)}
              disabled={isProcessing}
              className="w-full px-3 py-2 bg-dark-900 border border-dark-600 rounded-lg text-white focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {availableTiers
                .filter(t => t.status === 'active')
                .map((tier) => (
                  <option key={tier.id} value={tier.id}>
                    {tier.name} ({formatPrice(tier.price_monthly_cents)})
                    {tier.id === user.subscription_tier_id ? ' - Current' : ''}
                  </option>
                ))}
            </select>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-dark-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="px-4 py-2 text-white bg-dark-700 hover:bg-dark-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            ref={confirmButtonRef}
            onClick={handleConfirm}
            disabled={isProcessing || !isChanged}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? 'Changing...' : 'Change Tier'}
          </button>
        </div>
      </div>
    </div>
  );
}
